import { RenderEngine } from "./RenderEngine";
import { AnimationClip, LoopOnce, Object3D } from "three";
import { nodeHasVisibleMeshes } from "../ulti";

export enum AnimationDirection {
  Forward = 1,
  Backward = -1,
  Toggle = 0
}

export class AnimationController {
  private tokens:any[] = [];
  private completionCallbacks:(()=>void)[] = [];

  constructor(private engine:RenderEngine) {
  }

  acquireToken():any {
    const token = {};
    if (this.tokens.length === 0) {
      this.changeAASettings();
    }
    this.tokens.push(token);
    return token;
  }

  releaseToken(token:any):void {
    this.tokens = this.tokens.filter(t => t !== token);
    if (this.tokens.length === 0) {
      this.restoreAASettings();
      while (this.completionCallbacks.length > 0) {
        this.completionCallbacks.shift()();
      }
    }
  }

  playAnimationsByName(names:string[], direction = AnimationDirection.Toggle):Promise<void> {
    const clipsAll:AnimationClip[] = this.getActiveAnimationClips();
    const clips = names.map(name => clipsAll.find(clip => clip.name == name));

    for (const name of names) {
      if (clipsAll.find(c => c.name === name) === undefined) {
        console.error(`Cannot find clip: '${name}'`)
      }
    }

    return this.playAnimationClips(clips, direction); 
  }

  playAnimationsByAncestorObject(ancestor:Object3D, direction = AnimationDirection.Toggle):Promise<void> {
    const clipsAll = this.getActiveAnimationClips();
    const clips = [];

    for (const clip of clipsAll) {
      for (const track of clip.tracks) {
        // Remove the final .scale, .position, .quaternion etc from the name
        const parts = track.name.split(".");
        parts.pop();
        const name = parts.join(".");

        const node = ancestor.getObjectByName(name);
        if (node && nodeHasVisibleMeshes(node)) {
          clips.push(clip);
          break;
        }
      }
    }

    return this.playAnimationClips(clips, direction);
  }

  public waitForCompletion():Promise<void> {
    if (this.tokens.length === 0) return Promise.resolve(); 

    return new Promise<void>((resolve) => {
      this.completionCallbacks.push(resolve);
    });
  }

  getActiveAnimationClips() {
    // HACK: Access geometry service animations
  
    const c = this.engine.configurator;
    const animations:{id:string, clips:AnimationClip[]}[] = (c as any)._geometryService._animations;

    const geometryIds = c.config.groupings.reduce((all, cur) => {
      if (all.indexOf(cur.geometry_id)<0) {
          all.push(cur.geometry_id);
      }
      return all;
    }, [] as string[]);

    const clips = animations.reduce((all, cur) => {
      if (geometryIds.indexOf(cur.id)>=0) {
        all.push(...cur.clips);
      }
      return all;
    }, [] as AnimationClip[]);

    return clips;
  }

  private playAnimationClips(clips:AnimationClip[], direction:AnimationDirection = AnimationDirection.Toggle):Promise<void> {
    const render = (this.engine.configurator as any)._renderService;
    const mixer = render.animationController.mixer;
    const actions = [];

    return new Promise<void>((resolve) => {
      clips.forEach(clip => {
        const action = mixer.existingAction(clip) || mixer.clipAction(clip);

        actions.push(action);

        if (!action.isRunning()) {
          const token = this.acquireToken();
          const callback = () => {
            if (!action.isRunning()) {
              this.releaseToken(token);
              mixer.removeEventListener('finished', callback);

              // Resolve once all requested animation clips completed.
              if (actions.map(a => !a.isRunning()).reduce((a, b) => a && b, true)) {
                resolve();
              }
            }
          };
          mixer.addEventListener('finished', callback);
        }

        // initial setup
        if (!action.paused && !action.isRunning()) {
          if (direction === AnimationDirection.Toggle) {
            direction = AnimationDirection.Forward;
          } else {
            action.time = (direction == AnimationDirection.Forward) ? 0 : action.getClip().duration;
          }
          action.timeScale = direction as number;
          action.clampWhenFinished = true;
          action.loop = LoopOnce;
          action.play();
        } else {
          if (direction === AnimationDirection.Toggle) {
            action.timeScale = -Math.sign(action.timeScale);
          } else {
            action.time = (direction == AnimationDirection.Forward) ? 0 : action.getClip().duration;
            action.timeScale = direction as number;
          }
          action.paused = false;
        }
      });
    });
  }

  forceAnimationsPositionByAncestorObject(ancestor:Object3D, end:boolean):void {
    const clipsAll = this.getActiveAnimationClips();
    const clips = [];

    for (const clip of clipsAll) {
      for (const track of clip.tracks) {
        // Remove the final .scale, .position, .quaternion etc from the name
        const parts = track.name.split(".");
        parts.pop();
        const name = parts.join(".");

        if (ancestor.getObjectByName(name)) {
          clips.push(clip);
          break;
        }
      }
    }

    this.forceAnimationClipsPosition(clips, end);
  }

  private forceAnimationClipsPosition(clips:AnimationClip[], end:boolean):void {
    const render = (this.engine.configurator as any)._renderService;
    const mixer = render.animationController.mixer;

    const token = this.acquireToken();

    for (const clip of clips) {
      const action = mixer.existingAction(clip) || mixer.clipAction(clip);

      // +Inf / -Inf don't seem to work so uh....
      action.timeScale = end ? 100000000 : -100000000;

      if (action.isRunning() || action.paused) {
        action.paused = false;
      } else {
        action.clampWhenFinished = true;
        action.loop = LoopOnce;
        action.play();
      }
    }

    mixer.update(0);

    this.releaseToken(token);
  }

  private restoreAASettings = () => {};

  private changeAASettings():void {
    const antialias = this.engine.engine.services.render.settings.antialias;

    const originalAntighostMotionOnly1 = antialias.webgl1.temporal.antighostMotionOnly;
    const originalAntighostMotionOnly2 = antialias.webgl2.temporal.antighostMotionOnly;
    const originalTAASamples1 = antialias.webgl1.temporal.samples;
    const originalTAASamples2 = antialias.webgl1.temporal.samples;

    antialias.webgl1.temporal.antighostMotionOnly = false;
    antialias.webgl2.temporal.antighostMotionOnly = false;
    antialias.webgl1.temporal.samples = 4;
    antialias.webgl2.temporal.samples = 4;

    this.restoreAASettings = () => {
      antialias.webgl1.temporal.antighostMotionOnly = originalAntighostMotionOnly1;
      antialias.webgl2.temporal.antighostMotionOnly = originalAntighostMotionOnly2;
      antialias.webgl1.temporal.samples = originalTAASamples1;
      antialias.webgl2.temporal.samples = originalTAASamples2;
      this.restoreAASettings = () => {};
    }
  }
}